import { useCallback, useMemo } from 'react';
import {
  addMonths,
  addYears,
  eachDayOfInterval,
  endOfWeek,
  isAfter,
  isBefore,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';

import { useMonthViewActions, useMonthViewState } from 'stores/dischargeCalendarStore';

import useSnfLaunchDate from './useSnfLaunchDate';

export interface UseMonthNavigationResult {
  startDate: Date;
  endDate: Date;
  calendarDays: Date[];
  goToNextMonth: () => void;
  goToPreviousMonth: () => void;
  goToToday: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
  isCurrentMonth: boolean;
  isInCurrentMonth: (date: Date) => boolean;
}

export default function useMonthNavigation(): UseMonthNavigationResult {
  const { startDate, endDate } = useMonthViewState();
  const actions = useMonthViewActions();
  const snfLaunchDate = useSnfLaunchDate();

  // Calendar grid: starts on Sunday of the week containing the first of the month
  // and ends on Saturday of the week containing the last of the month
  const calendarStart = useMemo(() => startOfWeek(startDate, { weekStartsOn: 0 }), [startDate]);
  const calendarEnd = useMemo(() => endOfWeek(endDate, { weekStartsOn: 0 }), [endDate]);

  const calendarDays = useMemo(
    () => eachDayOfInterval({ start: calendarStart, end: calendarEnd }),
    [calendarStart, calendarEnd]
  );

  const todayMonthStart = useMemo(() => startOfMonth(new Date()), []);

  const isCurrentMonth = useMemo(() => startDate.getTime() === todayMonthStart.getTime(), [startDate, todayMonthStart]);

  const isInCurrentMonth = useCallback((date: Date) => isSameMonth(date, startDate), [startDate]);

  const canGoPrevious = useMemo(() => {
    if (!snfLaunchDate) return true;
    const previousMonthStart = subMonths(startDate, 1);
    // Allow navigation if the previous month contains or is after the launch date
    // Compare against the start of the month containing the launch date
    const launchDateMonthStart = startOfMonth(snfLaunchDate);
    return !isBefore(previousMonthStart, launchDateMonthStart);
  }, [startDate, snfLaunchDate]);

  const maxDate = useMemo(() => addYears(new Date(), 2), []);

  const canGoNext = useMemo(() => {
    const nextMonthStart = addMonths(startDate, 1);
    return !isAfter(nextMonthStart, startOfDay(maxDate));
  }, [startDate, maxDate]);

  const goToNextMonth = useCallback(() => {
    if (canGoNext) {
      actions.goToNextMonth();
    }
  }, [canGoNext, actions]);

  const goToPreviousMonth = useCallback(() => {
    if (canGoPrevious) {
      actions.goToPreviousMonth();
    }
  }, [canGoPrevious, actions]);

  return {
    startDate,
    endDate,
    calendarDays,
    goToNextMonth,
    goToPreviousMonth,
    goToToday: actions.goToToday,
    canGoNext,
    canGoPrevious,
    isCurrentMonth,
    isInCurrentMonth,
  };
}
